import React from "react";
import ReportTable from "../ReportTable";
import { principleTitles, renderYesNo, safeText } from "./helpers";
import usePrincipleData from "./usePrincipleData";
import useReportingYearLabels from "./useReportingYearLabels";

const formatDateDDMMYYYY = (value) => {
  if (!value) return "N/A";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return safeText(value, "N/A");
  const day = String(dt.getDate()).padStart(2, "0");
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const year = dt.getFullYear();
  return `${day}/${month}/${year}`;
};

const Principle8 = ({ styles }) => {
  const { loading, principle } = usePrincipleData(8);
  const { currentYear, previousYear } = useReportingYearLabels();
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};

  const fyCurrentColLabel = currentYear
    ? `In FY ${currentYear} (Current Financial Year)`
    : "Current Financial Year (N/A)";
  const fyPreviousColLabel = previousYear
    ? `In FY ${previousYear} (Previous Financial Year)`
    : "Previous Financial Year (N/A)";

  const essentialQ1Rows = (
    Array.isArray(essential?.socialImpactAssessments)
      ? essential.socialImpactAssessments
      : []
  ).map((row, idx) => ({
    _id: row?._id || `p8-e1-${idx}`,
    projectName: safeText(row?.projectName, "N/A"),
    notification: safeText(row?.SIANotificationNo, "N/A"),
    date: formatDateDDMMYYYY(row?.dateOfNotification),
    externalAgency: renderYesNo(row?.conductedByExternalAgency),
    publicDomain: renderYesNo(row?.resultsCommunicatedPublicDomain),
    webLink: safeText(row?.webLink, "N/A"),
  }));

  const essentialQ2Rows = (
    Array.isArray(essential?.rehabilitationAndResettlement)
      ? essential.rehabilitationAndResettlement
      : []
  ).map((row, idx) => ({
    _id: row?._id || `p8-e2-${idx}`,
    projectName: safeText(row?.projectName, "N/A"),
    state: safeText(row?.state, "N/A"),
    district: safeText(row?.district, "N/A"),
    paf: safeText(row?.numberOfProjectAffectedFamilies, "N/A"),
    covered: safeText(row?.percentagePAFsCoveredByRR, "N/A"),
    amount: safeText(row?.amountsPaidToPAFs, "N/A"),
  }));

  const essentialQ4Rows = [
    ["Directly sourced from MSMEs / small producers", "smallProducers"],
    [
      "Sourced directly from within the district and neighbouring districts",
      "neighbouringDistricts",
    ],
  ].map(([location, key], idx) => ({
    _id: `p8-e4-${idx}`,
    location,
    current: safeText(
      essential?.sourceFromSupplier?.[key]?.currentFinacialYear,
      "N/A",
    ),
    previous: safeText(
      essential?.sourceFromSupplier?.[key]?.previousFinancialYear,
      "N/A",
    ),
  }));

  const essentialQ5Rows = [
    ["Rural", "rural"],
    ["Semi-Urban", "semiUrban"],
    ["Urban", "urban"],
    ["Metropolitan", "metropolitan"],
  ].map(([location, key], idx) => {
    const row = essential?.jobCreationInSmallerTowns?.[key] || {};
    const current =
      row.currentFinacialYear ??
      row.currentFinancialYear ??
      row.current_financial_year;
    const previous =
      row.previousFinancialYear ??
      row.previousFinacialYear ??
      row.previous_financial_year;
    return {
      _id: `p8-e5-${idx}`,
      location,
      current: safeText(current, "N/A"),
      previous: safeText(previous, "N/A"),
    };
  });

  const leadershipQ1Rows = (
    Array.isArray(leadership?.socialImpactAssessments)
      ? leadership.socialImpactAssessments
      : []
  ).map((row, idx) => ({
    _id: row?._id || `p8-l1-${idx}`,
    details: safeText(row?.details, "N/A"),
    correctiveActionTaken: safeText(row?.correctiveActionTaken, "N/A"),
  }));

  const leadershipQ2Rows = (
    Array.isArray(leadership?.csrProjectsInAspirationalDistricts)
      ? leadership.csrProjectsInAspirationalDistricts
      : []
  ).map((row, idx) => ({
    _id: row?._id || `p8-l2-${idx}`,
    state: safeText(row?.state, "N/A"),
    district: safeText(row?.aspirationalDistrict, "N/A"),
    amountSpent: safeText(row?.amountSpent, "N/A"),
  }));

  const leadershipQ4Rows = (
    Array.isArray(leadership?.traditionalKnowledgeIntellectualProperties)
      ? leadership.traditionalKnowledgeIntellectualProperties
      : []
  ).map((row, idx) => ({
    _id: row?._id || `p8-l4-${idx}`,
    ip: safeText(row?.ipBasedOnTraditionalKnowledge, "N/A"),
    ownedAcquired: renderYesNo(row?.ownedAcquired),
    benefitShared: renderYesNo(row?.benefitShared),
    basis: safeText(row?.basisOfCalculatingBenefitShare, "N/A"),
  }));

  const leadershipQ5Rows = (
    Array.isArray(leadership?.correctiveActionsForAdverseOrders)
      ? leadership.correctiveActionsForAdverseOrders
      : []
  ).map((row, idx) => ({
    _id: row?._id || `p8-l5-${idx}`,
    authority: safeText(row?.authorityName, "N/A"),
    caseBrief: safeText(row?.caseBrief, "N/A"),
    correctiveAction: safeText(row?.correctiveActionTaken, "N/A"),
  }));

  const leadershipQ6Rows = (
    Array.isArray(leadership?.beneficiariesOfCSRProjects)
      ? leadership.beneficiariesOfCSRProjects
      : []
  ).map((row, idx) => ({
    _id: row?._id || `p8-l6-${idx}`,
    project: safeText(row?.csrProjectName, "N/A"),
    beneficiaries: safeText(row?.numberOfBeneficiaries, "N/A"),
    vulnerablePct: safeText(
      row?.percentageFromVulnerableMarginalizedGroups,
      "N/A",
    ),
  }));

  return (
    <div className={styles.principleBlock}>
      <div
        className={styles.principleHeading}
      >{`PRINCIPLE 8: ${principleTitles[7]}`}</div>
      {loading ? (
        <div className={styles.meta}>Loading principle 8...</div>
      ) : null}

      <div className={styles.band}>I. ESSENTIAL INDICATOR</div>

      <div className={styles.question}>
        1. Details of Social Impact Assessments (SIA) of projects undertaken by
        the entity based on applicable laws, in the current financial year.
      </div>
      {essentialQ1Rows.length ? (
        essentialQ1Rows.map((row) => (
          <React.Fragment key={row._id}>
            <div className={styles.question}>
              1. Name and brief details of project
            </div>
            <div className={styles.answer}>{row.projectName}</div>
            <div className={styles.question}>2. SIA Notification No.</div>
            <div className={styles.answer}>{row.notification}</div>
            <div className={styles.question}>
              3. Date of notification (dd/mm/yyyy)
            </div>
            <div className={styles.answer}>{row.date}</div>
            <div className={styles.question}>
              4. Whether conducted by independent external agency . (Yes / No)
            </div>
            <div className={styles.answer}>{row.externalAgency}</div>
            <div className={styles.question}>
              5. Results communicated in public domain (Yes / No).
            </div>
            <div className={styles.answer}>{row.publicDomain}</div>
            <div className={styles.question}>6. Relevant Web link</div>
            <div className={styles.answer}>{row.webLink}</div>
          </React.Fragment>
        ))
      ) : (
        <div className={styles.answer}>N/A</div>
      )}

      <div className={styles.question}>
        2. Provide information on project(s) for which ongoing Rehabilitation
        and Resettlement (R&R) is being undertaken by your entity, in the
        following format:
      </div>
      {essentialQ2Rows.length ? (
        essentialQ2Rows.map((row) => (
          <React.Fragment key={row._id}>
            <div className={styles.question}>1. Project Name</div>
            <div className={styles.answer}>{row.projectName}</div>
            <div className={styles.question}>2. State</div>
            <div className={styles.answer}>{row.state}</div>
            <div className={styles.question}>3. District</div>
            <div className={styles.answer}>{row.district}</div>
            <div className={styles.question}>
              4. No. of Project Affected Families (PAFs)
            </div>
            <div className={styles.answer}>{row.paf}</div>
            <div className={styles.question}>5. % of PAFs covered by R&R</div>
            <div className={styles.answer}>{row.covered}</div>
            <div className={styles.question}>6. Amounts Paid to PAFs</div>
            <div className={styles.answer}>{row.amount}</div>
          </React.Fragment>
        ))
      ) : (
        <div className={styles.answer}>N/A</div>
      )}

      <div className={styles.question}>
        3. Describe the mechanisms to receive and redress grievances of the
        community.
      </div>
      <div className={styles.answer}>
        {safeText(essential?.communityGrievanceMechanisms, "N/A")}
      </div>

      <div className={styles.question}>
        4. Percentage of input material (inputs to total inputs by value)
        sourced from suppliers:
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "location", label: "Location" },
          { field: "current", label: "Current financial year" },
          { field: "previous", label: "Previous financial year" },
        ]}
        rows={essentialQ4Rows}
      />

      <div className={styles.question}>
        5. Job creation in smaller towns – Disclose wages paid to persons
        employed (including employees or workers employed on a permanent or
        non-permanent / on contract basis) in the following locations, as % of
        total wage cost
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "location", label: "Location" },
          { field: "current", label: fyCurrentColLabel },
          { field: "previous", label: fyPreviousColLabel },
        ]}
        rows={essentialQ5Rows}
      />

      <div className={styles.band}>II. LEADERSHIP INDICATOR</div>

      <div className={styles.question}>
        1. Provide details of actions taken to mitigate any negative social
        impacts identified in the Social Impact Assessments (Reference: Question
        1 of Essential Indicators above):
      </div>
      <ReportTable
        showSerialNumber={true}
        columns={[
          {
            field: "details",
            label: "Details of negative social impact identified",
          },
          { field: "correctiveActionTaken", label: "Corrective action taken" },
        ]}
        rows={leadershipQ1Rows}
      />

      <div className={styles.question}>
        2. Provide the following information on CSR projects undertaken by your
        entity in designated aspirational districts as identified by government
        bodies:
      </div>
      <ReportTable
        showSerialNumber={true}
        columns={[
          { field: "state", label: "State" },
          { field: "district", label: "Aspirational District" },
          { field: "amountSpent", label: "Amount spent (In INR)" },
        ]}
        rows={leadershipQ2Rows}
      />

      <div className={styles.question}>
        3. (a) Do you have a preferential procurement policy where you give
        preference to purchase from suppliers comprising marginalized /
        vulnerable groups? (Yes / No)
      </div>
      <div
        className={styles.answer}
      >{`3. (b) From which marginalized / vulnerable groups do you procure?
${safeText(leadership?.procurementPolicy?.vulnerableGoupProcure, "N/A")}

3. (c) What percentage of total procurement (by value) does it constitute?
${safeText(leadership?.procurementPolicy?.totalProcurementByValue, "N/A")}

Policy available: ${safeText(
        leadership?.procurementPolicy?.action,
        "N/A",
      )}`}</div>

      <div className={styles.question}>
        4. Details of the benefits derived and shared from the intellectual
        properties owned or acquired by your entity (in the current financial
        year), based on traditional knowledge:
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          {
            field: "ip",
            label: "Intellectual Property based on traditional knowledge",
          },
          { field: "ownedAcquired", label: "Owned/Acquired (Yes / No)" },
          { field: "benefitShared", label: "Benefit shared (Yes / No)" },
          { field: "basis", label: "Basis of calculating benefit share" },
        ]}
        rows={leadershipQ4Rows}
      />

      <div className={styles.question}>
        5. Details of corrective actions taken or underway, based on any adverse
        order in intellectual property related disputes wherein usage of
        traditional knowledge is involved.
      </div>
      <ReportTable
        showSerialNumber={true}
        columns={[
          { field: "authority", label: "Name of authority" },
          { field: "caseBrief", label: "Brief of the Case" },
          { field: "correctiveAction", label: "Corrective action taken" },
        ]}
        rows={leadershipQ5Rows}
      />

      <div className={styles.question}>
        6. Details of beneficiaries of CSR Projects:
      </div>
      <ReportTable
        showSerialNumber={true}
        columns={[
          { field: "project", label: "CSR Project" },
          {
            field: "beneficiaries",
            label: "No. of persons benefitted from CSR projects",
          },
          {
            field: "vulnerablePct",
            label: "% of beneficiaries from vulnerable and marginalised groups",
          },
        ]}
        rows={leadershipQ6Rows}
      />
      <div className={styles.pageBreak} />
    </div>
  );
};

export default Principle8;
